import type { TFunction } from 'i18next'
import type { VirtualIpCharacterSelectionBatch } from '../../api'

export function formatCharacterBatchTitle(
  t: TFunction<'virtualIp'>,
  roundIndex: number,
  isLatest: boolean,
): string {
  if (isLatest) return t('characterHistory.latestBatch', { round: roundIndex })
  return t('characterHistory.batch', { round: roundIndex })
}

export function formatCandidatePlanLabel(
  t: TFunction<'virtualIp'>,
  roundIndex: number,
  candidateIndex: number,
): string {
  return t('characterHistory.planLabel', { round: roundIndex, index: candidateIndex })
}

/** Unified generation history display — newest batch first (created_at DESC, id DESC). */
export const VIRTUAL_IP_CHARACTER_BATCHES_PER_PAGE = 2

export function sortCharacterBatchesNewestFirst<T extends Pick<VirtualIpCharacterSelectionBatch, 'generation_batch_id' | 'created_at'>>(
  batches: T[],
): T[] {
  return batches.slice().sort((a, b) => {
    const at = Date.parse(a.created_at || '') || 0
    const bt = Date.parse(b.created_at || '') || 0
    if (bt !== at) return bt - at
    return String(b.generation_batch_id).localeCompare(String(a.generation_batch_id))
  })
}

export function paginateCharacterBatches<T>(batches: T[], page: number, pageSize = VIRTUAL_IP_CHARACTER_BATCHES_PER_PAGE): {
  page: number
  pageSize: number
  totalPages: number
  items: T[]
} {
  const totalPages = Math.max(1, Math.ceil(batches.length / pageSize))
  const safePage = Math.min(Math.max(1, page), totalPages)
  const start = (safePage - 1) * pageSize
  return {
    page: safePage,
    pageSize,
    totalPages,
    items: batches.slice(start, start + pageSize),
  }
}

export function findBatchPageForSelection(
  batches: VirtualIpCharacterSelectionBatch[],
  selectedId: string | null,
  pageSize = VIRTUAL_IP_CHARACTER_BATCHES_PER_PAGE,
): number | null {
  if (!selectedId) return null
  const sorted = sortCharacterBatchesNewestFirst(batches)
  const batchIndex = sorted.findIndex((batch) =>
    (batch.candidates ?? []).some((c) => c.candidate_id === selectedId || c.asset_id === selectedId),
  )
  if (batchIndex < 0) return null
  return Math.floor(batchIndex / pageSize) + 1
}
